import { useEffect, useState, useCallback } from "react";
import { Send, Mail, MessageSquare, Inbox, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate } from "../lib/api";
import { useResource } from "../lib/hooks";
import { PageHeader, Pill } from "../components/Common";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";

export default function Conversations() {
  const [convos, setConvos] = useState([]);
  const [active, setActive] = useState(null);
  const [thread, setThread] = useState([]);
  const [text, setText] = useState("");
  const [channel, setChannel] = useState("email");
  const contacts = useResource("contacts");

  const loadConvos = useCallback(async () => setConvos((await api.get("/conversations")).data), []);
  const loadThread = useCallback(async (c) => setThread((await api.get(`/conversations/${c.id}`)).data), []);
  useEffect(() => { loadConvos(); }, [loadConvos]);
  useEffect(() => { if (active) loadThread(active); }, [active, loadThread]);

  const send = async (inbound) => {
    if (!text.trim()) return;
    try {
      await api.post(`/conversations/${active.id}/${inbound ? "simulate-inbound" : "send"}`, { channel, text });
      setText("");
      await loadThread(active);
      loadConvos();
      if (!inbound) toast.success(channel === "sms" ? "SMS queued (Twilio MOCK)" : "Message sent");
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <PageHeader overline="Inbox" title="Conversations" desc="Unified email & SMS inbox. Opted-out contacts are automatically blocked (Spam Act 2003)." />
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 h-[calc(100vh-15rem)] min-h-[500px]">
        <div className="lg:col-span-4 rounded-md border bg-card/60 flex flex-col overflow-hidden">
          <div className="p-3 border-b">
            <select data-testid="start-conversation-select" value="" onChange={(e) => setActive(contacts.items.find((c) => c.id === e.target.value))}
              className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm">
              <option value="">+ Start conversation with contact…</option>
              {contacts.items.map((c) => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>)}
            </select>
          </div>
          <div className="overflow-y-auto flex-1">
            {!convos.length && <div className="p-6 text-sm text-muted-foreground flex items-center gap-2"><Inbox className="h-4 w-4" />No conversations yet.</div>}
            {convos.map((c) => (
              <button key={c.contact.id} onClick={() => setActive(c.contact)} data-testid={`conversation-${c.contact.id}`}
                className={`w-full text-left p-3 border-b hover:bg-secondary transition-colors ${active?.id === c.contact.id ? "bg-secondary" : ""}`}>
                <div className="flex justify-between"><span className="text-sm font-medium">{c.contact.first_name} {c.contact.last_name}</span>
                  {c.unread > 0 && <span className="text-[10px] rounded-full bg-accent text-accent-foreground px-1.5">{c.unread}</span>}</div>
                <div className="text-xs text-muted-foreground truncate">{c.channel === "sms" ? "SMS" : "Email"} · {c.last}</div>
              </button>
            ))}
          </div>
        </div>
        <div className="lg:col-span-8 rounded-md border bg-card/60 flex flex-col overflow-hidden">
          {!active ? <div className="m-auto text-muted-foreground text-sm">Select a conversation</div> : (
            <>
              <div className="p-4 border-b flex justify-between items-center">
                <div><div className="font-display font-bold" data-testid="thread-contact-name">{active.first_name} {active.last_name}</div>
                  <div className="text-xs text-muted-foreground">{active.email} · {active.phone}</div></div>
                {active.unsubscribed && <Pill tone="red">opted out</Pill>}
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="message-thread">
                {thread.map((m) => (
                  <div key={m.id} className={`max-w-[75%] rounded-md p-3 text-sm ${m.direction === "outbound" ? "ml-auto bg-primary/20 border border-primary/30" : "bg-secondary"}`}>
                    <div className="whitespace-pre-wrap">{m.text}</div>
                    <div className="text-[10px] text-muted-foreground mt-1 font-mono">{m.channel} · {m.status} · {fmtDate(m.created_at)}</div>
                  </div>
                ))}
              </div>
              <div className="p-3 border-t space-y-2">
                <div className="flex gap-2">
                  <Button size="sm" variant={channel === "email" ? "default" : "secondary"} onClick={() => setChannel("email")} data-testid="channel-email"><Mail className="h-3.5 w-3.5 mr-1" />Email</Button>
                  <Button size="sm" variant={channel === "sms" ? "default" : "secondary"} onClick={() => setChannel("sms")} data-testid="channel-sms"><MessageSquare className="h-3.5 w-3.5 mr-1" />SMS</Button>
                </div>
                <Textarea data-testid="message-input" rows={3} value={text} onChange={(e) => setText(e.target.value)} placeholder="Type a message…" />
                <div className="flex justify-end gap-2">
                  <Button size="sm" variant="outline" onClick={() => send(true)} data-testid="simulate-inbound-btn"><Plus className="h-3.5 w-3.5 mr-1" />Log inbound reply</Button>
                  <Button size="sm" onClick={() => send(false)} data-testid="send-message-btn"><Send className="h-3.5 w-3.5 mr-1" />Send</Button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
